import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowLeft,
  faBars,
  faBarcode,
  faCamera,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircleInfo,
  faCopy,
  faDownLeftAndUpRightToCenter,
  faEllipsis,
  faFileExport,
  faHouse,
  faImages,
  faLayerGroup,
  faLocationDot,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faMicrophone,
  faMinus,
  faPaperclip,
  faPen,
  faPlus,
  faPrint,
  faQrcode,
  faRotateRight,
  faSitemap,
  faSquarePlus,
  faStar,
  faTrashCan,
  faUpRightAndDownLeftFromCenter,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Repo-wide icon strategy (ticket 07): FontAwesome free-solid only, no
 * @mui/icons-material. Each icon is a tiny component so call sites read the
 * same as before; explicit imports keep the bundle tree-shaken.
 */
interface IconProps {
  /** MUI-style size, mapped to the shell's ~20px icon scale (spec §8). */
  fontSize?: "small" | "medium" | "large" | "inherit";
  /** CSS color; MUI's palette "action" maps to the surrounding currentColor. */
  color?: string;
}

/**
 * Icon sizes in rem so they follow the root font size instead of the label
 * they sit next to — nav labels are 0.68rem, the glyph stays ~20px.
 */
function iconSize(fontSize: IconProps["fontSize"]): string | undefined {
  if (fontSize === "small") return "1.25rem";
  if (fontSize === "medium") return "1.5rem";
  if (fontSize === "large") return "2rem";
  return undefined;
}

function make(icon: IconDefinition) {
  return function Icon({ fontSize, color }: IconProps) {
    const size = iconSize(fontSize);
    const faColor = color === "action" ? "currentColor" : color;
    return (
      <FontAwesomeIcon
        icon={icon}
        style={size ? { fontSize: size } : undefined}
        color={faColor}
      />
    );
  };
}

export const ArrowBackIcon = make(faArrowLeft);
export const MenuIcon = make(faBars);
export const PlusIcon = make(faPlus);
export const HomeIcon = make(faHouse);
export const AccountTreeIcon = make(faSitemap);
/** Catalogs destination + page (catalog-selector 02). */
export const LayerGroupIcon = make(faLayerGroup);
export const AddBoxIcon = make(faSquarePlus);
export const DeleteSweepIcon = make(faTrashCan);
export const PrintIcon = make(faPrint);
export const QrCodeIcon = make(faQrcode);
export const BarcodeScannerIcon = make(faQrcode);
export const BarcodeIcon = make(faBarcode);
export const RemoveIcon = make(faMinus);
export const AddIcon = make(faPlus);
export const FileDownloadIcon = make(faFileExport);
export const PhotoLibraryIcon = make(faImages);
export const InfoOutlinedIcon = make(faCircleInfo);
export const DeleteOutlineIcon = make(faTrashCan);
export const PhotoCameraIcon = make(faCamera);
export const MicIcon = make(faMicrophone);
export const AttachFileIcon = make(faPaperclip);
export const CheckIcon = make(faCheck);
export const ChevronRightIcon = make(faChevronRight);
export const ChevronLeftIcon = make(faChevronLeft);
export const ChevronDownIcon = make(faChevronDown);
export const ChevronUpIcon = make(faChevronUp);
export const CopyIcon = make(faCopy);
export const EditIcon = make(faPen);
export const CloseIcon = make(faXmark);
export const RotateRightIcon = make(faRotateRight);
export const MoreHorizIcon = make(faEllipsis);
export const StarIcon = make(faStar);
export const ZoomInIcon = make(faMagnifyingGlassPlus);
export const ZoomOutIcon = make(faMagnifyingGlassMinus);
export const LocationDotIcon = make(faLocationDot);
/**
 * Sidebar collapse control: arrows pointing into the centre ("shrink the
 * panel"). Explicit rather than chevrons — the chevron pair read as back/next
 * on the reported shell (scan-followups 01).
 */
export const CollapseNavIcon = make(faDownLeftAndUpRightToCenter);
/** Sidebar expand control: arrows pointing out of the centre ("grow the panel"). */
export const ExpandNavIcon = make(faUpRightAndDownLeftFromCenter);
